import SubdirectoryArrowRight from '@mui/icons-material/SubdirectoryArrowRight';
import { Box, Card, Grid, Paper, Typography } from '@mui/material';
import {
  ArrayField,
  BooleanField,
  ChipField,
  Datagrid,
  DateField,
  FunctionField,
  Labeled,
  Loading,
  SelectField,
  ShowBase,
  TabProps,
  TabbedShowLayout,
  TextField,
  useRecordContext,
} from 'react-admin';

import CraftTwoColumns from 'src/components/CraftTwoColumns';
import ArchivedOrDeletedNotification from 'src/components/ArchivedOrDeletedNotification';
import { CraftPageHeader } from 'src/components/CraftPageHeader';
import { CraftPageSection } from 'src/components/CraftPageSection';
import { DATA_PACKAGE_KINDS } from 'src/utils/defaults/Constants';
import { DataPackageRecord } from 'src/types';
import { Center } from 'src/components/Center';

const Aside = () => (
  <Box ml={2} minWidth="25rem">
    <Card sx={{ p: 2 }}>
      <CraftTwoColumns
        left={
          <>
            <Labeled label="Created" fullWidth>
              <DateField source="created_at" showTime />
            </Labeled>
            <Labeled label="Deleted">
              <DateField source="deleted_at" emptyText="N/A" showTime />
            </Labeled>
          </>
        }
        right={
          <Labeled label="Updated">
            <DateField source="updated_at" showTime />
          </Labeled>
        }
      />
    </Card>
  </Box>
);

type DataPackageApiFieldXref = DataPackageRecord['api_fields_xrefs'][number];

const renderApiFieldNameField = (fieldXref: DataPackageApiFieldXref) => {
  const { path } = fieldXref.api_field;
  const pathSections = path.split('.');
  const nestedLevel = pathSections.length - 1;
  const pathName = pathSections.at(-1);

  return (
    <Typography>
      {nestedLevel > 0 && (
        <Box component="span" sx={{ ml: nestedLevel * 2, mr: 1 }}>
          <SubdirectoryArrowRight />
        </Box>
      )}
      {pathName}
    </Typography>
  );
};

const DataPackageApiFieldsShowTab = (props: Omit<TabProps, 'children' | 'label'>) => (
  <TabbedShowLayout.Tab label="Fields" {...props}>
    <CraftPageSection title="API Fields">
      <Paper sx={{ mb: 2 }}>
        <ArrayField label={false} source="api_fields_xrefs" sort={{ field: 'api_field.path', order: 'ASC' }}>
          <Datagrid bulkActionButtons={false} empty={<Center my={2}>No API fields enabled</Center>}>
            <FunctionField label="Name" source="api_field.path" sortable={false} render={renderApiFieldNameField} />
            <ChipField label="Sources" source="policies.sources" emptyText=" " sortable={false} />
            <ChipField label="Filters" source="policies.filters" emptyText=" " sortable={false} />
          </Datagrid>
        </ArrayField>
      </Paper>
    </CraftPageSection>
  </TabbedShowLayout.Tab>
);

const ShowContent = () => {
  const record = useRecordContext();

  if (!record) return <Loading />;

  return (
    <>
      <CraftPageHeader>Data Package Info</CraftPageHeader>

      <Box mx={2}>
        <Grid container>
          <Grid item md={8}>
            <Card sx={{ pb: 2 }}>
              <TabbedShowLayout>
                <TabbedShowLayout.Tab label="Details">
                  <CraftPageSection title="General">
                    <CraftTwoColumns
                      left={
                        <>
                          <Labeled>
                            <TextField label="Name" source="name" />
                          </Labeled>
                          <Labeled>
                            <SelectField label="Kind" source="kind" choices={DATA_PACKAGE_KINDS} />
                          </Labeled>
                        </>
                      }
                      leftSX={{ gap: 2 }}
                      right={
                        <Labeled>
                          <BooleanField label="Deprecated" source="is_deprecated" />
                        </Labeled>
                      }
                    />
                  </CraftPageSection>
                </TabbedShowLayout.Tab>

                <DataPackageApiFieldsShowTab />
              </TabbedShowLayout>
            </Card>
          </Grid>

          <Grid item md={4}>
            <Aside />
          </Grid>
        </Grid>
      </Box>

      <ArchivedOrDeletedNotification record={record} notificationText="This Data Package is DELETED" />
    </>
  );
};

export const DataPackagesShow = () => (
  <ShowBase>
    <ShowContent />
  </ShowBase>
);

export default DataPackagesShow;
